import {
  Calendar,
  CheckCircle,
  Download,
  FileText,
  Flag,
  Image,
  Mail,
  MessageSquare,
  TrendingUp,
  User,
  Users,
  XCircle,
  X,
  CheckSquare,
  AlertCircle,
  DollarSign,
  Repeat,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import useLocale from '../contexts/LocaleContext';
import { useAssessment, useUpdateAssessmentFlag } from '../hooks/useAssessments';
import { assessmentsApi, reportsApi, complianceApi } from '../lib/api-client';
import toast from 'react-hot-toast';

// Image Dialog Component
function ImageDialog({ imageUrl, onClose }: { imageUrl: string; onClose: () => void }) {
  useEffect(() => {
    // Prevent body scroll when dialog is open
    document.body.style.overflow = 'hidden';

    // Handle ESC key to close
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div
      className='fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md p-4'
      onClick={onClose}
    >
      <div className='relative w-full h-full flex items-center justify-center'>
        <button
          onClick={onClose}
          className='absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70'
          aria-label='Close image'
        >
          <X className='h-6 w-6' />
        </button>
        <div className='w-full h-full max-w-[90vw] max-h-[90vh] flex items-center justify-center'>
          <img
            src={imageUrl}
            alt='Enlarged view'
            className='max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl'
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '90vh' }}
          />
        </div>
      </div>
    </div>
  );
}

export default function AssessmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: assessment, isLoading } = useAssessment(id!);
  const { dictionary } = useLocale();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const updateFlagMutation = useUpdateAssessmentFlag();

  // Fetch compliance checklist status for COMPLIANCE assessments
  const { data: checklistStatus } = useQuery({
    queryKey: ['compliance-checklist', assessment?.facilityId],
    queryFn: () => complianceApi.getChecklistStatus(assessment!.facilityId),
    enabled: !!assessment && assessment.assessmentType === 'COMPLIANCE',
  });

  // Fetch assessment history for this facility — same type only so chart stays consistent
  const { data: assessmentHistoryResponse } = useQuery({
    queryKey: ['facility-assessment-history', assessment?.facilityId, assessment?.assessmentType],
    queryFn: () => assessmentsApi.getAll({
      facilityId: assessment?.facilityId,
      assessmentType: assessment?.assessmentType as 'HYGIENE' | 'COMPLIANCE',
      limit: 100,
    }),
    enabled: !!assessment?.facilityId && !!assessment?.assessmentType,
  });

  const assessmentHistory = assessmentHistoryResponse?.data || [];

  // Format data for the chart
  const chartData =
    assessmentHistory.length > 0
      ? [...assessmentHistory]
          .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
          .map(a => ({
            date: new Date(a.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            score: Number(a.percentageScore.toFixed(1)),
            fullDate: new Date(a.createdAt).toLocaleString(),
          }))
      : [];

  if (isLoading) {
    return (
      <div className='p-6'>
        <Card>
          <CardContent className='p-6'>
            <p className='text-gray-500'>{dictionary['assessment_detail.loading']}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className='p-6'>
        <Card>
          <CardContent className='p-6'>
            <p className='text-red-600'>{dictionary['assessment_detail.not_found']}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDownloadPDF = async () => {
    if (!id) return;
    setIsDownloading(true);
    try {
      const blob = await reportsApi.downloadAssessmentPDF(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assessment-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSharePDF = async () => {
    if (!id) return;
    setIsSharing(true);
    try {
      const data = await reportsApi.shareAssessmentPDF(id);
      toast.success(`Report sent to ${data.to}`);
    } catch {
      toast.error('Failed to share report');
    } finally {
      setIsSharing(false);
    }
  };

  // Calculate clean status based on score: >= 50% is clean, < 50% is not clean
  const isClean = assessment.percentageScore >= 50;

  // Get photo URLs from assessmentPhotos array
  const photoUrls = assessment.assessmentPhotos?.map((photo: any) => photo.url) || assessment.photos || [];

  // Format requirement type for display
  const formatRequirementType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Format final decision for display
  const formatFinalDecision = (decision: string) => {
    if (!decision) return 'Not specified';
    return decision
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className='p-4 sm:p-6 max-w-7xl mx-auto space-y-6'>
      {/* Image Dialog */}
      {selectedImage && <ImageDialog imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />}

      {/* Header */}
      <div className='border-b pb-4'>
        <div className='flex items-center gap-3 flex-wrap'>
          <h1 className='text-2xl font-bold text-gray-900'>{dictionary['assessment_detail.title']}</h1>
          {assessment.assessmentType && (
            <div
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-sm font-semibold ${
                assessment.assessmentType === 'HYGIENE'
                  ? 'bg-secondary text-primary border-primary/30'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}
            >
              <span>
                {assessment.assessmentType === 'HYGIENE'
                  ? (dictionary['assessment_detail.type_hygiene'] ?? 'Hygiene')
                  : (dictionary['assessment_detail.type_compliance'] ?? 'Compliance')}
              </span>
            </div>
          )}
          {assessment.isReassessment && (
            <div className='inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 border border-purple-300 rounded-md text-sm font-semibold'>
              <Repeat className='h-4 w-4' />
              <span>Reassessment</span>
            </div>
          )}
          {assessment.isFlagged && (
            <div className='inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-600 border border-red-400 rounded-md text-sm font-semibold'>
              <Flag className='h-4 w-4' />
              <span>{dictionary['dashboard.flagged'] ?? 'Flagged'}</span>
            </div>
          )}
          <Button
            variant='outline'
            size='sm'
            onClick={() => updateFlagMutation.mutate({ id: assessment.id, isFlagged: !assessment.isFlagged })}
            disabled={updateFlagMutation.isPending}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border ${
              assessment.isFlagged
                ? 'bg-red-50 text-red-600 border-red-400 hover:bg-red-100'
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 hover:border-red-300 hover:text-red-600'
            }`}
          >
            <Flag className='h-4 w-4' />
            <span>{assessment.isFlagged ? (dictionary['dashboard.unflag'] ?? 'Unflag') : (dictionary['dashboard.flagged'] ?? 'Flag')}</span>
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
          >
            <Download className='h-4 w-4' />
            <span>{isDownloading ? 'Downloading...' : 'Download PDF'}</span>
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={handleSharePDF}
            disabled={isSharing}
            className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
          >
            <Mail className='h-4 w-4' />
            <span>{isSharing ? 'Sharing...' : 'Share'}</span>
          </Button>
        </div>
        <p className='text-sm text-gray-600 mt-1'>{dictionary['assessment_detail.subtitle']}</p>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Left Column - Main Content */}
        <div className='lg:col-span-2 space-y-6'>
          {/* Facility Info */}
          <Card>
            <CardHeader>
              <CardTitle className='text-lg'>{dictionary['assessment_detail.facility_info']}</CardTitle>
            </CardHeader>
            <CardContent>
              {assessment.facility && (
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm'>
                  <div>
                    <span className='text-gray-500 text-xs font-medium'>
                      {dictionary['assessment_detail.facility_name']}
                    </span>
                    <p className='font-medium text-gray-900 mt-1'>{assessment.facility.title}</p>
                  </div>
                  {assessment.facility.location && (
                    <div>
                      <span className='text-gray-500 text-xs font-medium'>
                        {dictionary['assessment_detail.location']}
                      </span>
                      <p className='font-medium text-gray-900 mt-1'>
                        {assessment.facility.location.name}{' '}
                        <span className='text-gray-500 text-xs'>({assessment.facility.location.locationType})</span>
                      </p>
                    </div>
                  )}
                  {assessment.facility.facilityType && (
                    <div>
                      <span className='text-gray-500 text-xs font-medium'>
                        {dictionary['assessment_detail.facility_type']}
                      </span>
                      <p className='font-medium text-gray-900 mt-1'>{assessment.facility.facilityType.name}</p>
                    </div>
                  )}
                  {assessment.coordinates && (
                    <div>
                      <span className='text-gray-500 text-xs font-medium'>Coordinates</span>
                      <p className='font-medium text-gray-900 mt-1 text-xs font-mono'>{assessment.coordinates}</p>
                      <a
                        href={`https://www.google.com/maps?q=${assessment.coordinates}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-xs text-primary hover:text-primary/80 underline mt-0.5 inline-block'
                      >
                        View on Map
                      </a>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Questions & Answers (Hygiene assessments only) */}
          {(assessment.assessmentType === 'HYGIENE' || (!assessment.assessmentType && assessment.facilityAssessmentQuestionAnswers?.length)) && (
          <Card>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <CardTitle className='text-lg'>{dictionary['assessment_detail.answers']}</CardTitle>
                <span className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded'>
                  {assessment.facilityAssessmentQuestionAnswers?.length || 0} questions
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {assessment.facilityAssessmentQuestionAnswers &&
              assessment.facilityAssessmentQuestionAnswers.length > 0 ? (
                <div className='space-y-4'>
                  {assessment.facilityAssessmentQuestionAnswers
                    .filter(answer => !answer.FacilityAssessmentQuestion?.parentQuestionId)
                    .map((answer, index) => {
                      const subquestionAnswers = assessment.facilityAssessmentQuestionAnswers.filter(
                        sub =>
                          sub.FacilityAssessmentQuestion?.parentQuestionId === answer.FacilityAssessmentQuestion?.id &&
                          sub.FacilityAssessmentQuestion?.triggerOptionId === answer.selectedOption?.id
                      );

                      return (
                        <div key={answer.id} className='pb-4 border-b border-gray-100 last:border-b-0 last:pb-0'>
                          <div className='flex items-start justify-between gap-3'>
                            <div className='flex-1 min-w-0'>
                              <p className='text-sm font-medium text-gray-900'>
                                <span className='text-gray-400 mr-2'>{index + 1}.</span>
                                {answer.FacilityAssessmentQuestion?.question ||
                                  dictionary['assessment_detail.question']}
                              </p>
                              {answer.FacilityAssessmentQuestion && (
                                <p className='text-xs text-gray-500 mt-1'>
                                  Weight: {answer.FacilityAssessmentQuestion.weight}
                                </p>
                              )}
                            </div>
                            {answer.selectedOption && (
                              <div className='flex-shrink-0'>
                                <span className='inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200'>
                                  {answer.selectedOption.option} · {answer.selectedOption.marks}pts
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Subquestions */}
                          {subquestionAnswers.length > 0 && (
                            <div className='ml-6 mt-3 space-y-2 pl-3 border-l-2 border-gray-200'>
                              {subquestionAnswers.map((subAnswer, subIndex) => (
                                <div key={subAnswer.id} className='bg-gray-50 rounded p-2.5'>
                                  <div className='flex items-start justify-between gap-2'>
                                    <p className='text-xs font-medium text-gray-700 flex-1'>
                                      <span className='text-gray-400 mr-1.5'>
                                        {index + 1}.{subIndex + 1}
                                      </span>
                                      {subAnswer.FacilityAssessmentQuestion?.question}
                                      {subAnswer.FacilityAssessmentQuestion && (
                                        <span className='text-gray-500 ml-1.5'>
                                          (Weight: {subAnswer.FacilityAssessmentQuestion.weight})
                                        </span>
                                      )}
                                    </p>
                                    {subAnswer.selectedOption && (
                                      <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white text-gray-600 border border-gray-200 whitespace-nowrap'>
                                        {subAnswer.selectedOption.option} · {subAnswer.selectedOption.marks}pts
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className='text-sm text-gray-400 text-center py-6'>{dictionary['assessment_detail.no_answers']}</p>
              )}
            </CardContent>
          </Card>
          )}

          {/* Compliance (Compliance assessments only) */}
          {(assessment.assessmentType === 'COMPLIANCE' || (!assessment.assessmentType && assessment.complianceResponses?.length)) &&
            assessment.complianceResponses &&
            assessment.complianceResponses.length > 0 && (
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-lg'>
                    {dictionary['assessment_detail.compliance'] ?? 'Compliance'}
                  </CardTitle>
                  <span className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded'>
                    {assessment.complianceResponses.length} questions
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className='space-y-4'>
                  {assessment.complianceResponses.map((resp: any) => (
                    <div
                      key={resp.id}
                      className='flex items-start justify-between gap-3 pb-4 border-b border-gray-100 last:border-b-0 last:pb-0'
                    >
                      <p className='text-sm font-medium text-gray-900 flex-1 min-w-0'>
                        {resp.complianceQuestion?.question ?? '—'}
                      </p>
                      {resp.selectedOption && (
                        <span className='inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 flex-shrink-0'>
                          {resp.selectedOption.option}
                          {resp.selectedOption.marks != null && ` · ${resp.selectedOption.marks}pts`}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Performance Trend */}
          {chartData.length > 1 && (
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-lg flex items-center gap-2'>
                    <TrendingUp className='h-5 w-5 text-primary' />
                    Performance Trend
                  </CardTitle>
                  <span className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded'>
                    {chartData.length} assessments
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width='100%' height={280}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                    <XAxis dataKey='date' tick={{ fontSize: 12 }} stroke='#6b7280' />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fontSize: 12 }}
                      stroke='#6b7280'
                      label={{ value: 'Score (%)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '8px',
                      }}
                      formatter={(value: number) => [`${value}%`, 'Score']}
                      labelFormatter={(label, payload) => {
                        if (payload && payload.length > 0) {
                          return payload[0].payload.fullDate;
                        }
                        return label;
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar
                      dataKey='score'
                      fill='#3b82f6'
                      radius={[8, 8, 0, 0]}
                      name='Score (%)'
                      animationDuration={800}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <div className='mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200'>
                  <div className='flex items-start gap-2'>
                    <TrendingUp className='h-4 w-4 text-gray-500 mt-0.5' />
                    <div className='text-xs text-gray-600'>
                      {chartData.length > 1 && (
                        <>
                          {chartData[chartData.length - 1].score > chartData[chartData.length - 2].score ? (
                            <span className='text-green-600 font-medium'>
                              ↗ Improved by{' '}
                              {(chartData[chartData.length - 1].score - chartData[chartData.length - 2].score).toFixed(
                                1
                              )}
                              %
                            </span>
                          ) : chartData[chartData.length - 1].score < chartData[chartData.length - 2].score ? (
                            <span className='text-red-600 font-medium'>
                              ↘ Decreased by{' '}
                              {(chartData[chartData.length - 2].score - chartData[chartData.length - 1].score).toFixed(
                                1
                              )}
                              %
                            </span>
                          ) : (
                            <span className='text-gray-600 font-medium'>→ No change</span>
                          )}
                          {' from previous assessment'}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Score & Info */}
        <div className='space-y-6'>
          {/* Score Card */}
          <Card>
            <CardHeader>
              <CardTitle className='text-lg'>{dictionary['assessment_detail.score']}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='text-center py-4'>
                <div className='text-5xl font-bold text-gray-900 mb-3'>{assessment.percentageScore.toFixed(1)}%</div>
                <div className='flex items-center justify-center gap-2'>
                  {isClean ? (
                    <>
                      <CheckCircle className='h-5 w-5 text-green-600' />
                      <span className='text-base text-green-600 font-semibold'>
                        {dictionary['assessment_detail.clean'] || 'Clean'}
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle className='h-5 w-5 text-red-600' />
                      <span className='text-base text-red-600 font-semibold'>
                        {dictionary['assessment_detail.not_clean'] || 'Not Clean'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assessment Info */}
          <Card>
            <CardHeader>
              <CardTitle className='text-lg'>{dictionary['assessment_detail.assessment_info']}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4 text-sm'>
              {/* Carried By */}
              {assessment.carriedBy && (
                <div className='flex items-start gap-3 pb-4 border-b border-gray-100'>
                  <User className='h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs text-gray-500 font-medium'>Assessed By</p>
                    <p className='text-gray-900 font-medium mt-1'>{assessment.carriedBy.name}</p>
                    {assessment.carriedBy.email && (
                      <p className='text-xs text-gray-500 mt-0.5'>{assessment.carriedBy.email}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Date & Time */}
              <div className='flex items-start gap-3 pb-4 border-b border-gray-100'>
                <Calendar className='h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0' />
                <div className='min-w-0 flex-1'>
                  <p className='text-xs text-gray-500 font-medium'>Assessment Date</p>
                  <p className='text-gray-900 font-medium mt-1'>
                    {new Date(assessment.createdAt).toLocaleDateString()}
                  </p>
                  <p className='text-xs text-gray-500 mt-0.5'>{new Date(assessment.createdAt).toLocaleTimeString()}</p>
                  {assessment.weekNumber && assessment.year && (
                    <p className='text-xs text-gray-600 mt-1'>
                      Week {assessment.weekNumber}, {assessment.year}
                    </p>
                  )}
                </div>
              </div>

              {/* Questionnaire */}
              {assessment.questionnaire && (
                <div className='flex items-start gap-3 pb-4 border-b border-gray-100'>
                  <FileText className='h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs text-gray-500 font-medium'>{dictionary['assessment_detail.questionnaire']}</p>
                    <p className='text-gray-900 font-medium mt-1'>{assessment.questionnaire.name}</p>
                  </div>
                </div>
              )}

              {/* Decisions / Final Decision */}
              {((assessment.decisions && assessment.decisions.length > 0) || assessment.finalDecision) && (
                <div className='flex items-start gap-3 pb-4 border-b border-gray-100'>
                  <AlertCircle className='h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs text-gray-500 font-medium'>
                      {assessment.decisions?.length > 1
                        ? (dictionary['assessment_detail.decisions'] ?? 'Decisions')
                        : (dictionary['assessment_detail.final_decision'] ?? 'Final Decision')}
                    </p>
                    <p className='text-gray-900 font-medium mt-1'>
                      {assessment.decisions?.length
                        ? assessment.decisions.map(d => formatFinalDecision(d)).join(', ')
                        : formatFinalDecision(assessment.finalDecision!)}
                    </p>
                    {assessment.chargedAmount && (
                      <p className='text-xs text-gray-600 mt-1 flex items-center gap-1'>
                        <DollarSign className='h-3 w-3' />
                        Amount: {assessment.chargedAmount}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {assessment.recommendations && assessment.recommendations.length > 0 && (
                <div className='flex items-start gap-3 pb-4 border-b border-gray-100'>
                  <FileText className='h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs text-gray-500 font-medium mb-2'>
                      {dictionary['assessment_detail.recommendations'] ?? 'Recommendations'}
                    </p>
                    <ul className='space-y-2 mt-1'>
                      {assessment.recommendations.map((rec: { id: string; recommendation: string; deadline: string | null }) => (
                        <li key={rec.id} className='bg-gray-50 rounded-lg p-2.5 border border-gray-100'>
                          <p className='text-gray-900 text-sm'>{rec.recommendation}</p>
                          {rec.deadline && (
                            <p className='text-xs text-gray-500 mt-1'>
                              {dictionary['assessment_detail.deadline'] ?? 'Deadline'}: {new Date(rec.deadline).toLocaleDateString()}
                              {new Date(rec.deadline).toLocaleTimeString() !== '12:00:00 AM' &&
                                ` ${new Date(rec.deadline).toLocaleTimeString()}`}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Follow-up */}
              {assessment.followupDate && (
                <div className='flex items-start gap-3 pb-4 border-b border-gray-100'>
                  <Calendar className='h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs text-gray-500 font-medium'>Follow-up Date</p>
                    <p className='text-gray-900 font-medium mt-1'>
                      {new Date(assessment.followupDate).toLocaleDateString()}
                    </p>
                    <div className='mt-2'>
                      {new Date(assessment.followupDate) < new Date() ? (
                        <span className='inline-block text-xs bg-red-50 text-red-600 px-2 py-1 rounded border border-red-200'>
                          Overdue
                        </span>
                      ) : (
                        <span className='inline-block text-xs bg-green-50 text-green-600 px-2 py-1 rounded border border-green-200'>
                          Upcoming
                        </span>
                      )}
                    </div>
                    {assessment.isFollowupCarried && assessment.followupCarriedAt && (
                      <p className='text-xs text-green-600 mt-2 font-medium'>
                        Completed: {new Date(assessment.followupCarriedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Committee */}
              {assessment.committee && assessment.committee.length > 0 && (
                <div className='flex items-start gap-3 pb-4 border-b border-gray-100'>
                  <Users className='h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs text-gray-500 font-medium mb-2'>Committee Members</p>
                    <div className='mt-1 space-y-2'>
                      {assessment.committee.map((member: string, index: number) => {
                        // Parse member string: "Name (Role) - Phone"
                        const parts = member.split(' - ');
                        const nameAndRole = parts[0] || member;
                        const phone = parts[1] || null;
                        const roleMatch = nameAndRole.match(/\(([^)]+)\)/);
                        const name = roleMatch ? nameAndRole.replace(/\s*\([^)]+\)\s*/, '').trim() : nameAndRole;
                        const role = roleMatch ? roleMatch[1] : null;

                        return (
                          <div key={index} className='bg-gray-50 rounded-lg p-2.5 border border-gray-100'>
                            <p className='text-gray-900 font-medium text-sm'>{name}</p>
                            {role && <p className='text-xs text-gray-600 mt-0.5'>{role}</p>}
                            {phone && (
                              <p className='text-xs text-gray-500 mt-1 flex items-center gap-1'>
                                <span className='font-medium'>Phone:</span> {phone}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Comment */}
              {assessment.additionalComment && (
                <div className='flex items-start gap-3'>
                  <MessageSquare className='h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs text-gray-500 font-medium'>Additional Comment</p>
                    <p className='text-gray-900 text-sm mt-1 whitespace-pre-wrap'>{assessment.additionalComment}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Requirements */}
          {assessment.requirements && assessment.requirements.length > 0 && (
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-lg flex items-center gap-2'>
                    <CheckSquare className='h-5 w-5 text-primary' />
                    Requirements
                  </CardTitle>
                  <span className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded'>
                    {assessment.requirements.length}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className='space-y-2.5'>
                  {assessment.requirements.map(req => (
                    <div
                      key={req.id}
                      className='flex items-start gap-2.5 bg-gray-50 border border-gray-100 rounded-lg p-3'
                    >
                      {req.isAvailable ? (
                        <CheckCircle className='h-4 w-4 text-green-600 mt-0.5 flex-shrink-0' />
                      ) : (
                        <XCircle className='h-4 w-4 text-red-600 mt-0.5 flex-shrink-0' />
                      )}
                      <div className='flex-1 min-w-0'>
                        <div className='text-sm font-medium text-gray-900'>
                          {formatRequirementType(req.requirementType)}
                        </div>
                        <div className='text-xs text-gray-600 mt-0.5'>
                          {req.isAvailable ? 'Available' : 'Not Available'}
                        </div>
                        {req.notes && <div className='text-xs text-gray-500 mt-1.5 italic'>{req.notes}</div>}
                        {(req.hasLand !== null || req.hasFunds !== null || req.estimatedCompletion) && (
                          <div className='text-xs text-gray-500 mt-1.5 space-y-0.5'>
                            {req.hasLand !== null && <div>Land: {req.hasLand ? 'Yes' : 'No'}</div>}
                            {req.hasFunds !== null && <div>Funds: {req.hasFunds ? 'Yes' : 'No'}</div>}
                            {req.estimatedCompletion && (
                              <div>Est. completion: {new Date(req.estimatedCompletion).toLocaleDateString()}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Compliance Status */}
          {assessment.assessmentType === 'COMPLIANCE' && checklistStatus && (
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-lg flex items-center gap-2'>
                    <CheckSquare className='h-5 w-5 text-amber-600' />
                    Compliance Status
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className='space-y-4'>
                {checklistStatus.available.length > 0 && (
                  <div>
                    <p className='text-xs font-medium text-green-700 mb-2'>Passed ({checklistStatus.available.length})</p>
                    <ul className='space-y-1.5'>
                      {checklistStatus.available.map(q => (
                        <li key={q.id} className='flex items-start gap-2 text-sm'>
                          <CheckCircle className='h-4 w-4 text-green-600 mt-0.5 flex-shrink-0' />
                          <span className='text-gray-700'>{q.question}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {checklistStatus.unavailable.length > 0 && (
                  <div>
                    <p className='text-xs font-medium text-amber-700 mb-2'>Outstanding ({checklistStatus.unavailable.length})</p>
                    <ul className='space-y-1.5'>
                      {checklistStatus.unavailable.map(q => (
                        <li key={q.id} className='flex items-start gap-2 text-sm'>
                          <AlertCircle className='h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0' />
                          <span className='text-gray-700'>{q.question}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Photos */}
          {photoUrls && photoUrls.length > 0 && (
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-lg flex items-center gap-2'>
                    <Image className='h-5 w-5 text-primary' />
                    Photos
                  </CardTitle>
                  <span className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded'>{photoUrls.length}</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-2 gap-2'>
                  {photoUrls.map((photoUrl: any, index: any) => (
                    <div
                      key={index}
                      className='relative group aspect-square cursor-pointer'
                      onClick={() => setSelectedImage(photoUrl)}
                    >
                      <img
                        src={photoUrl}
                        alt={`Photo ${index + 1}`}
                        className='w-full h-full object-cover rounded-lg border border-gray-200 hover:opacity-90 transition-opacity'
                      />
                      <div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg flex items-center justify-center pointer-events-none'>
                        <span className='text-white opacity-0 group-hover:opacity-100 text-xs font-medium bg-black bg-opacity-50 px-2 py-1 rounded'>
                          View
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Signature */}
          {assessment.signaturePhotoUrl && (
            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>{dictionary['assessment_detail.signature']}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='cursor-pointer group' onClick={() => setSelectedImage(assessment.signaturePhotoUrl)}>
                  <img
                    src={assessment.signaturePhotoUrl}
                    alt='Signature'
                    className='border border-gray-200 rounded-lg max-w-full hover:opacity-90 transition-opacity'
                  />
                  <p className='text-xs text-gray-500 mt-2 text-center group-hover:text-gray-700 transition-colors'>
                    Click to view full size
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
